import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./therapistsQuestionsReports.module.css";
import i18n from "../../i18n";
import {
  useCreateBulkReportsMutation,
  useGetTherapistsQuestionsReportsQuery,
  useGetTherapistUnreportedAppointmentsQuery,
} from "../../services/api/user.api";
import { useGetCompanyByIdQuery } from "../../services/api/utils.api";
import {
  useGetActiveQuestionnaireScoreStatusesQuery,
  useUpdateQuestionnaireScoreTotalAmountMutation,
} from "../../services/api/payment.api";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import Loader from "../loader/loader";
import { getPrintDate } from "../../utils/utils";
import editIcon from "../../images/edit_icon.svg";
import { TTherapistQuestionReport } from "../../services/types/user.types";
import ListPagination from "../listPagination/listPagination";
import { TLimits } from "../../types/types";
import { TUpdateQuestionnaireScoreTotalAmountRequest } from "../../services/types/payment.types";
import { roundPrice } from "../../utils/constants";
import ExportTableButtons from "../exportTableButtons/ExportTableButtons";
import BulkAppointmentsReportsPopup from "../modals/bulkAppointmentsReports/bulkAppointmentsReports";
import Modal from "../modals/modal/Modal";

type TStatusOption = { value: number; label: string };
type TFilledOption = { value: string; label: string };

const LIMIT_NUMBER = 10;
type TimeRangeMode = "today" | "month" | "custom";

const filterSelectStyles = {
  menu: (base: object) => ({ ...base, fontSize: "0.875rem" }),
  option: (base: object) => ({ ...base, fontSize: "0.875rem", padding: "0.4rem 0.75rem" }),
  singleValue: (base: object) => ({ ...base, fontSize: "0.875rem" }),
};

// ── Subcomponents ────────────────────────────────────────────────────────────

type TReportRowProps = {
  report: TTherapistQuestionReport;
  index: number;
  statusOptions: TStatusOption[];
  amountDrafts: Record<number, string>;
  isUpdating: boolean;
  canEditChargingFields: boolean;
  onEdit: (report: TTherapistQuestionReport) => void;
  onUpdate: (report: TTherapistQuestionReport, patch: { questionnaireCompaniesScoreUsersStatusesId?: number; totalAmount?: number }) => void;
  onAmountChange: (id: number, value: string) => void;
  onAmountBlur: (report: TTherapistQuestionReport, value: string) => void;
  isAppointmentsMode: boolean;
};

function ReportRow({
  report, index, statusOptions, amountDrafts,
  isUpdating, canEditChargingFields,
  onEdit, onUpdate, onAmountChange, onAmountBlur, isAppointmentsMode
}: TReportRowProps) {
  return (
    <li className={`${styles.inquiry} ${index % 2 === 0 ? styles.inquiry_colored : ""} ${report.StatusId === 5 ? styles.inquiryEmphasized : ""}`}>
      <p className={`${styles.inquiryCell} ${styles.cellAnswered}`}>
        <span className={report.HasTherapistAnswered ? styles.answeredBadge : styles.notAnsweredBadge}>
          {report.HasTherapistAnswered ? t("THERAPISTS_REPORTS_ANSWERED_YES") : t("THERAPISTS_REPORTS_ANSWERED_NO")}
        </span>
      </p>
      <p className={`${styles.inquiryCell} ${styles.cellTherapist}`}>{report.TherapistName}</p>
      <p className={`${styles.inquiryCell} ${styles.cellUser}`}>{report.UserFirstName} {report.UserLastName}</p>
      <p className={`${styles.inquiryCell} ${styles.cellDate}`}>
        {report.Timestamp ? getPrintDate(new Date(report.Timestamp)) : "-"}
      </p>
      <p className={`${styles.inquiryCell} ${styles.cellDate_appointment}`}>
        {report.AppointmentDate ? getPrintDate(new Date(report.AppointmentDate)) : "-"}
      </p>
      <p className={`${styles.inquiryCell} ${styles.cellCycle}`}>{report.Cycle ?? "-"}</p>
      <div className={`${styles.inquiryCell} ${styles.cellReportStatus} ${report.StatusId === 5 ? styles.statusCellEmphasized : ""}`}>
        {!isAppointmentsMode && <span className={styles.statusSelectWrapper} onClick={(e) => e.stopPropagation()}>
          <Select<TStatusOption>
            classNamePrefix="status-select"
            options={statusOptions}
            value={statusOptions.find((o) => o.value === report.StatusId) ?? null}
            onChange={(selected: SingleValue<TStatusOption>) => {
              if (selected && selected.value !== report.StatusId)
                onUpdate(report, { questionnaireCompaniesScoreUsersStatusesId: selected.value });
            }}
            isDisabled={isUpdating || !canEditChargingFields || report.StatusId == null}
            isSearchable={false}
            menuPlacement="auto"
            menuPosition="fixed"
            menuPortalTarget={document.body}
          />
        </span>}
      </div>
      <p className={`${styles.inquiryCell} ${styles.cellQuestionnaire}`}>{report.QuestionnaireName ?? "-"}</p>
      <p className={`${styles.inquiryCell} ${styles.cellStatus}`}>
        {!isAppointmentsMode && <span className={styles.amountInputWrap}>
          ₪
          <input
            type="number"
            step="0.01"
            min="0"
            className={styles.inlineAmountInput}
            value={
              report.QuestionnaireCompaniesScoreUsersId != null && report.QuestionnaireCompaniesScoreUsersId in amountDrafts
                ? amountDrafts[report.QuestionnaireCompaniesScoreUsersId]
                : report.TotalAmount ?? ""
            }
            onChange={(e) => { e.stopPropagation(); onAmountChange(report.QuestionnaireCompaniesScoreUsersId!, e.target.value); }}
            onBlur={(e) => { e.stopPropagation(); onAmountBlur(report, e.target.value); }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            onClick={(e) => e.stopPropagation()}
            disabled={isUpdating || !canEditChargingFields || report.QuestionnaireCompaniesScoreUsersId == null}
          />
        </span>
        }
      </p>
      <p className={`${styles.inquiryCell} ${styles.cellRRM}`}>{report.RRM ?? "-"}</p>
      <p className={`${styles.inquiryCell} ${styles.cellPayment}`}>
        {report.TotalAmount != null && report.TotalAmount !== 0 && report.TotalAmountPay != null && report.TotalAmountPay !== 0 && report.TotalAmountPay < report.TotalAmount
          ? <span className={styles.partiallyPaidBadge}>{t("PARTIALLY_PAID_BADGE")}</span>
          : report.TotalAmountPay != null && report.TotalAmountPay !== 0
            ? <span className={styles.paidBadge}>{t("PAID_BADGE")}</span>
            : report.TotalAmount != null && report.TotalAmount !== 0
              ? <span className={styles.unpaidBadge}>{t("UNPAID_BADGE")}</span>
              : "-"}
      </p>
      <p className={`${styles.inquiryCell} ${styles.cellActions}`}>
        <button
          className={`${styles.editBtn} hover`}
          onClick={(e) => { e.stopPropagation(); onEdit(report); }}
          title={t("THERAPISTS_REPORTS_EDIT_BUTTON")}
        >
          <img src={editIcon} alt={t("THERAPISTS_REPORTS_EDIT_BUTTON")} />
        </button>
      </p>
    </li>
  );
}

type TAppointmentRowProps = {
  report: TTherapistQuestionReport;
  index: number;
  checked: boolean;
  onToggle: (id: number) => void;
};

function AppointmentRow({ report, index, checked, onToggle }: TAppointmentRowProps) {
  return (
    <li className={`${styles.inquiry} ${index % 2 === 0 ? styles.inquiry_colored : ""} ${styles.appointmentsGridTemplate}`}>
      <p className={`${styles.inquiryCell} ${styles.cellAnswered}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(report.AppointmentsId!)}
        />
      </p>
      <p className={`${styles.inquiryCell} ${styles.cellTherapist}`}>{report.TherapistName}</p>
      <p className={`${styles.inquiryCell} ${styles.cellUser}`}>{report.UserFirstName} {report.UserLastName}</p>
      <p className={`${styles.inquiryCell} ${styles.cellDate}`}>-</p>
      <p className={`${styles.inquiryCell} ${styles.cellDate_appointment}`}>
        {report.AppointmentDate ? getPrintDate(new Date(report.AppointmentDate)) : "-"}
      </p>
      <p className={`${styles.inquiryCell} ${styles.cellCycle}`}>{report.StartTime ?? "-"}</p>
    </li>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

function TherapistsQuestionsReports() {
  const navigate = useNavigate();
  const admin = useAppSelector(adminSelector);

  const { data: currentCompany } = useGetCompanyByIdQuery(admin.companyId, { skip: !admin.companyId });
  const canEditChargingFields = currentCompany?.isChargingCustomers === true;

  const [limits, setLimits] = useState<TLimits>({ start: 1, end: LIMIT_NUMBER });
  const [amountDrafts, setAmountDrafts] = useState<Record<number, string>>({});
  const [filters, setFilters] = useState({
    therapistName: "", userName: "", questionnaireName: "",
    statusId: "", filled: "", dateFrom: "", dateTo: "",
  });
  const [dateRangeMode, setDateRangeMode] = useState<TimeRangeMode | null>(null);
  const [submittedFilters, setSubmittedFilters] = useState<typeof filters | null>(null);
  const [openPaymentPopup, setOpenPaymentPopup] = useState(false);
  const hasFiltersToSubmit = useMemo(
    () =>
      Object.entries(filters)
        .filter(([k]) => k !== "dateFrom" && k !== "dateTo")
        .some(([, v]) => v !== "") ||
      dateRangeMode !== null,
    [filters, dateRangeMode]
  );
  const hasSubmittedFilters = submittedFilters !== null;
  const isAppointmentsMode = submittedFilters?.filled === "appointments";

  const { data: reports, isLoading: isLoadingReports, isError: isErrorReports, refetch: refetchReports } =
    useGetTherapistsQuestionsReportsQuery(submittedFilters ?? undefined, {
      skip: !hasSubmittedFilters || isAppointmentsMode,
      refetchOnMountOrArgChange: true,
    });

  const { data: unreportedAppointments, isLoading: isLoadingAppointments, isError: isErrorAppointments } =
    useGetTherapistUnreportedAppointmentsQuery(
      { therapistName: submittedFilters?.therapistName, userName: submittedFilters?.userName, dateFrom: submittedFilters?.dateFrom, dateTo: submittedFilters?.dateTo },
      { skip: !hasSubmittedFilters || !isAppointmentsMode, refetchOnMountOrArgChange: true }
    );

  const isLoading = isAppointmentsMode ? isLoadingAppointments : isLoadingReports;
  const isError = isAppointmentsMode ? isErrorAppointments : isErrorReports;

  const displayItems: TTherapistQuestionReport[] = useMemo(() => {
    if (!hasSubmittedFilters) return [];
    return isAppointmentsMode ? (unreportedAppointments ?? []) : (reports ?? []);
  }, [hasSubmittedFilters, isAppointmentsMode, unreportedAppointments, reports]);

  const paginatedItems = useMemo(
    () => displayItems.slice(limits.start - 1, limits.end),
    [displayItems, limits]
  );

  useEffect(() => { setLimits({ start: 1, end: LIMIT_NUMBER }); }, [submittedFilters]);

  // useEffect(() => {
  //   if (displayItems.length > 0 && limits.end > displayItems.length)
  //     setLimits({ start: 1, end: Math.min(LIMIT_NUMBER, displayItems.length) });
  // }, [displayItems.length, limits.end]);

  const { data: statuses } = useGetActiveQuestionnaireScoreStatusesQuery(undefined, {
    skip: isLoadingReports || isErrorReports,
  });

  const statusOptions: TStatusOption[] = useMemo(
    () => (statuses ?? []).map((s) => ({ value: s.id, label: s.name })),
    [statuses]
  );

  const statusNameById = useMemo(
    () => (statuses ?? []).reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {} as Record<number, string>),
    [statuses]
  );

  const filledOptions: TFilledOption[] = useMemo(() => [
    { value: "filled", label: t("THERAPISTS_REPORTS_ANSWERED_YES") },
    { value: "not-filled", label: t("THERAPISTS_REPORTS_ANSWERED_NO") },
    { value: "appointments", label: t("PENDING_REPORTS_APPOINTMENTS") },
  ], []);

  //check appointments
  const [checkedAppointmentIds, setCheckedAppointmentIds] = useState<Set<number>>(new Set());
  // reset when mode changes
  useEffect(() => {
    setCheckedAppointmentIds(new Set());
  }, [isAppointmentsMode]);

  const handleToggleAppointment = useCallback((id: number) => {
    setCheckedAppointmentIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const allChecked = checkedAppointmentIds.size === displayItems.length && displayItems.length > 0;
  const handleToggleAll = useCallback(() => {
    setCheckedAppointmentIds(
      allChecked
        ? new Set()
        : new Set(displayItems.map((item) => item.AppointmentsId!))
    );
  }, [allChecked, displayItems]);

  const [createBulkReports, { isLoading: isCreatingBulkReports }] = useCreateBulkReportsMutation();

  const [updateQuestionnaireScoreTotalAmount, { isLoading: isUpdating }] =
    useUpdateQuestionnaireScoreTotalAmountMutation();

  const handleSubmitFilters = useCallback((e: React.FormEvent) => {
      e.preventDefault();
      if (!hasFiltersToSubmit) return;     // ← guard first
    
      const today = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");

      const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
      const firstOfMonth = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`;
    
      const computedDateFrom =
        dateRangeMode === "today"  ? todayStr      :
        dateRangeMode === "month"  ? firstOfMonth  :
        dateRangeMode === "custom" ? filters.dateFrom : "";
    
      const computedDateTo =
        dateRangeMode === "today"  ? todayStr    :
        dateRangeMode === "month"  ? todayStr    :
        dateRangeMode === "custom" ? filters.dateTo : "";
    
      setSubmittedFilters({
        ...filters,
        therapistName: filters.therapistName.trim() || "",
        userName: filters.userName.trim() || "",
        dateFrom: computedDateFrom,
        dateTo: computedDateTo,
      });
      setLimits({ start: 1, end: LIMIT_NUMBER });
    }, [filters, dateRangeMode, hasFiltersToSubmit]);

  const clearFilters = useCallback(() => {
    setFilters({ therapistName: "", userName: "", questionnaireName: "", statusId: "", filled: "", dateFrom: "", dateTo: "" });
    setSubmittedFilters(null);
    setLimits({ start: 1, end: LIMIT_NUMBER });
    setDateRangeMode(null);
  }, []);

  const handleEdit = useCallback((report: TTherapistQuestionReport) => {
    navigate("/action-report", { state: { report } });
  }, [navigate]);

  const handleUpdateReport = useCallback(async (
    report: TTherapistQuestionReport,
    patch: { questionnaireCompaniesScoreUsersStatusesId?: number; totalAmount?: number }
  ) => {
    try {
      const body: TUpdateQuestionnaireScoreTotalAmountRequest = {
        totalAmount: patch.totalAmount ?? report.TotalAmount ?? 0,
        questionnaireCompaniesScoreUsersStatusesId:
          patch.questionnaireCompaniesScoreUsersStatusesId ?? report.StatusId!,
      };
      await updateQuestionnaireScoreTotalAmount({ id: report.QuestionnaireCompaniesScoreUsersId!, body }).unwrap();
      await refetchReports();
    } catch (err) {
      console.error("Failed to update report:", err);
    }
  }, [updateQuestionnaireScoreTotalAmount, refetchReports]);

  const handleAmountChange = useCallback((id: number, value: string) => {
    setAmountDrafts((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleAmountBlur = useCallback((report: TTherapistQuestionReport, raw: string) => {
    const num = raw === "" ? null : roundPrice(Number(raw));
    const current = report.TotalAmount ?? null;
    if (num !== null && num !== current)
      handleUpdateReport(report, { totalAmount: num });
    setAmountDrafts((prev) => {
      const next = { ...prev };
      delete next[report.QuestionnaireCompaniesScoreUsersId!];
      return next;
    });
  }, [handleUpdateReport]);

  if (isLoading) return <Loader />;

  if (isError) {
    return (
      <section className={styles.section} dir={i18n.dir()}>
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{t("THERAPISTS_REPORTS_ERROR")}</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section} dir={i18n.dir()}>
      <div className={styles.content}>
        <article className={styles.home__section}>
          <h4 className={styles.home__sectionTitle}>{t("THERAPISTS_QUESTIONS_REPORTS_PAGE_TITLE")}</h4>

          <form className={styles.filtersContainer} onSubmit={handleSubmitFilters}>
            <div className={styles.filtersRow}>
              <input type="text" className={styles.filterInput} placeholder={t("THERAPISTS_REPORTS_FILTER_THERAPIST_NAME")}
                value={filters.therapistName} onChange={(e) => setFilters({ ...filters, therapistName: e.target.value })} />
              <input type="text" className={styles.filterInput} placeholder={t("THERAPISTS_REPORTS_FILTER_USER_NAME")}
                value={filters.userName} onChange={(e) => setFilters({ ...filters, userName: e.target.value })} />
              <input type="text" className={styles.filterInput} placeholder={t("THERAPISTS_REPORTS_FILTER_QUESTIONNAIRE_NAME")}
                value={filters.questionnaireName} onChange={(e) => setFilters({ ...filters, questionnaireName: e.target.value })}
                disabled={filters.filled === "appointments"} />
              <Select<TStatusOption>
                className={styles.filterSelectWrap} classNamePrefix="filter-select"
                options={statusOptions} value={statusOptions.find((o) => o.value === Number(filters.statusId)) ?? null}
                onChange={(s: SingleValue<TStatusOption>) => setFilters({ ...filters, statusId: s ? String(s.value) : "" })}
                placeholder={t("THERAPISTS_REPORTS_FILTER_STATUS")} isSearchable={false} isClearable
                styles={filterSelectStyles} isDisabled={filters.filled === "appointments"} />
            </div>
            <div className={styles.filtersRow}>
              <div className={styles.dateRangeToggle}>
                {(["today", "month", "custom"] as TimeRangeMode[]).map((mode) => (
                  <label key={mode} className={styles.dateRangeLabel}>
                    <input
                      type="radio"
                      name="dateRangeMode"
                      checked={dateRangeMode === mode}
                      onChange={() => {
                        if (dateRangeMode === mode) {
                          // clicking the active radio deselects → back to "all"
                          setDateRangeMode(null);
                          setFilters((f) => ({ ...f, dateFrom: "", dateTo: "" }));
                        } else {
                          setDateRangeMode(mode);
                          if (mode !== "custom") setFilters((f) => ({ ...f, dateFrom: "", dateTo: "" }));
                        }
                      }}
                    />
                    {t(`REPORT_TIME_${mode.toUpperCase()}`) || mode}
                  </label>
                ))}
              </div>

              {dateRangeMode === "custom" && (
                <>
                  <input type="date" className={styles.filterInput}
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
                  <input type="date" className={styles.filterInput}
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
                </>
              )}              <Select<TFilledOption>
                className={styles.filterSelectWrap} classNamePrefix="filter-select"
                options={filledOptions} value={filledOptions.find((o) => o.value === filters.filled) ?? null}
                onChange={(s: SingleValue<TFilledOption>) => setFilters({ ...filters, filled: s ? s.value : "" })}
                placeholder={t("THERAPISTS_REPORTS_FILTER_ANSWERED")} isSearchable={false} isClearable
                styles={filterSelectStyles} />
              <button type="submit" className={styles.submitFiltersBtn} disabled={!hasFiltersToSubmit}>
                {t("THERAPISTS_REPORTS_SUBMIT_FILTERS")}
              </button>
              {hasSubmittedFilters && (
                <button type="button" className={`${styles.clearFiltersBtn} hover`} onClick={clearFilters}>
                  {t("THERAPISTS_REPORTS_CLEAR_FILTERS")}
                </button>
              )}
            </div>
          </form>

          {!isAppointmentsMode && (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "8px" }}>
              <ExportTableButtons
                entity="TherapistReports"
                dataRows={displayItems.map((report) => ({
                  Answered: report.HasTherapistAnswered ? t("THERAPISTS_REPORTS_ANSWERED_YES") : t("THERAPISTS_REPORTS_ANSWERED_NO"),
                  TherapistName: report.TherapistName,
                  UserName: `${report.UserFirstName} ${report.UserLastName}`,
                  Date: report.Timestamp ? getPrintDate(new Date(report.Timestamp)) : "-",
                  Cycle: report.Cycle ?? "-",
                  Questionnaire: report.QuestionnaireName ?? "-",
                  Status: statusNameById[report.StatusId!] ?? "-",
                  TotalAmount: report.TotalAmount ?? "-",
                  Payment:
                    report.TotalAmount != null && report.TotalAmount !== 0 && report.TotalAmountPay != null && report.TotalAmountPay !== 0 && report.TotalAmountPay < report.TotalAmount
                      ? t("PARTIALLY_PAID_BADGE")
                      : report.TotalAmountPay != null ? t("PAID_BADGE")
                        : report.TotalAmount != null ? t("UNPAID_BADGE")
                          : "-",
                }))}
              />
            </div>
          )}

          <div className={styles.tableWrapper}>
            <ul className={styles.inquiries}>
              {!hasSubmittedFilters ? (
                <p className={styles.text}>{t("THERAPISTS_REPORTS_DEFINE_FILTER")}</p>
              ) : displayItems.length === 0 ? (
                <p className={styles.text}>{t("THERAPISTS_REPORTS_NO_DATA")}</p>
              ) : (
                <>
                  <li className={`${styles.inquiry} ${styles.inquiryHeader} ${isAppointmentsMode&&styles.appointmentsGridTemplate}`}>
                    <p className={`${styles.inquiryCell} ${styles.cellAnswered}`}>
                      {isAppointmentsMode ? (
                        <>
                          <label className={styles.toggleLabel}>
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={handleToggleAll}
                            />
                            <span>{t('SELECT_ALL')}</span>
                          </label>

                          {checkedAppointmentIds.size > 0 && (
                            <button
                              className={`${styles.iconButton} btn btn_type_edit`}
                              onClick={() => setOpenPaymentPopup(true)}
                              disabled={checkedAppointmentIds.size === 0}
                              title={t('SUBMIT_BUTTON')}
                            />
                          )}
                        </>
                      ) : (
                        t('THERAPISTS_REPORTS_TABLE_ANSWERED')
                      )}
                    </p>
                    <p className={`${styles.inquiryCell} ${styles.cellTherapist}`}>{t("THERAPISTS_REPORTS_TABLE_THERAPIST")}</p>
                    <p className={`${styles.inquiryCell} ${styles.cellUser}`}>{t("THERAPISTS_REPORTS_TABLE_USER")}</p>
                    <p className={`${styles.inquiryCell} ${styles.cellDate}`}>{t("THERAPISTS_REPORTS_TABLE_DATE")}</p>
                    <p className={`${styles.inquiryCell} ${styles.cellDate_appointment}`}>{t("THERAPISTS_REPORTS_TABLE_DATE_APPOINTMENT")}</p>
                    <p className={`${styles.inquiryCell} ${styles.cellCycle}`}>{isAppointmentsMode ? t("START_TIME") : t("THERAPISTS_REPORTS_TABLE_CYCLE")}</p>
                    {!isAppointmentsMode && <p className={`${styles.inquiryCell} ${styles.cellReportStatus}`}>{t("THERAPISTS_REPORTS_TABLE_STATUS")}</p>}
                    {!isAppointmentsMode && <p className={`${styles.inquiryCell} ${styles.cellQuestionnaire}`}>{t("THERAPISTS_REPORTS_TABLE_QUESTIONNAIRE")}</p>}
                    {!isAppointmentsMode && <p className={`${styles.inquiryCell} ${styles.cellStatus}`}>{t("THERAPISTS_REPORTS_TABLE_MEETING_SUM")}</p>}
                    {!isAppointmentsMode && <p className={`${styles.inquiryCell} ${styles.cellRRM}`}>{t("THERAPISTS_REPORTS_TABLE_RRM")}</p>}
                    {!isAppointmentsMode && <p className={`${styles.inquiryCell} ${styles.cellPayment}`}>{t("THERAPISTS_REPORTS_TABLE_PAYMENT")}</p>} 
                    {!isAppointmentsMode && <p className={`${styles.inquiryCell} ${styles.cellActions}`}>{t("THERAPISTS_REPORTS_TABLE_ACTIONS")}</p>}
                  </li>
                  {paginatedItems.map((item, index) =>
                    isAppointmentsMode ? (
                      <AppointmentRow
                        key={item.AppointmentsId}
                        report={item}
                        index={index}
                        checked={checkedAppointmentIds.has(item.AppointmentsId!)}
                        onToggle={handleToggleAppointment}
                      />
                    ) : (
                      <ReportRow
                        key={item.Id}
                        report={item}
                        index={index}
                        statusOptions={statusOptions}
                        amountDrafts={amountDrafts}
                        isUpdating={isUpdating}
                        canEditChargingFields={canEditChargingFields}
                        onEdit={handleEdit}
                        onUpdate={handleUpdateReport}
                        onAmountChange={handleAmountChange}
                        onAmountBlur={handleAmountBlur}
                        isAppointmentsMode={isAppointmentsMode}
                      />
                    )
                  )}
                </>
              )}
            </ul>
          </div>
        </article>
      </div>
      {openPaymentPopup && (
        <Modal
          onClose={() => {
            setOpenPaymentPopup(false);
          }}
        >
          <BulkAppointmentsReportsPopup
            onClose={() => setOpenPaymentPopup(false)}
            onSubmit={async (paymentInfo) => {
              await createBulkReports({
                ...paymentInfo,
                appointmentIds: [...checkedAppointmentIds],
              }).unwrap();
              setOpenPaymentPopup(false);
              setCheckedAppointmentIds(new Set());
            }}
            numberOfAppointments={checkedAppointmentIds.size}
          />
        </Modal>
      )}

      {displayItems.length > 0 && (
        <ListPagination limits={limits} total={displayItems.length} setLimits={setLimits} step={LIMIT_NUMBER} />
      )}
    </section>
  );
}

export default TherapistsQuestionsReports;